import { RouteReuseStrategy } from '@angular/router';
import { RecreateOnParamChangeStrategy } from './core/routing/recreate-on-param-change.strategy';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideCheckNoChangesConfig,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideOptimus } from '@openng/optimus-ui/config';
import {
  NavigationError,
  provideRouter,
  withComponentInputBinding,
  withHashLocation,
  withNavigationErrorHandler,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { provideMarkdown } from 'ngx-markdown';

import { routes } from './app.routes';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { noCacheInterceptor } from './core/interceptors/no-cache.interceptor';
import { AuthService } from './core/services/auth.service';
import { CookieConsentService } from './core/services/cookie-consent.service';
import { ProductAnalyticsService } from './core/services/product-analytics.service';
import { FeatureFlagService } from './core/services/feature-flag.service';
import { DesignVersionService, initializeDesignVersion } from './core/services/design-version.service';
import { ResearchPopupService } from './core/services/research-popup.service';
import { providePostHogErrorHandler } from './core/posthog-error-handler';
import { MessageService } from '@openng/optimus-ui/api';
import { ViewportPreloadingStrategy } from './services/viewport-preloading-strategy.service';
import { firstValueFrom } from 'rxjs';
import { CurrencyPipe, DatePipe } from '@angular/common';

const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

async function runAuthInitializer(): Promise<void> {
  const authService = inject(AuthService);

  try {
    await firstValueFrom(authService.fetchCsrfToken());
  } catch (err) {
    console.error('Failed to fetch CSRF token:', err);
  }

  // Only probe /users/me when we have a non-sensitive session hint in
  // localStorage. Without this, every cold start on the login page would 401
  // on /users/me, trigger /auth/refresh (also 401), and call logout().
  if (authService.hasSessionHint()) {
    await authService.resolveUserFromSession();
  }
}

function runFeatureFlagInitializer(): void {
  inject(CookieConsentService); // ensures posthog.init runs before onFeatureFlags is registered
  inject(ProductAnalyticsService).start();
  inject(FeatureFlagService);
  inject(DesignVersionService);
}

function runResearchInitializer(): void {
  inject(ResearchPopupService);
}

/** Runs in an injection context, so the app's own ErrorHandler (PostHog) receives the failure. */
export function reportNavigationError({ url, error }: NavigationError): void {
  const reason = error instanceof Error ? error.message : String(error);
  inject(ErrorHandler).handleError(new Error(`Navigation to ${url} failed: ${reason}`, { cause: error }));
}

export const appConfig: ApplicationConfig = {
  providers: [
    DatePipe,
    CurrencyPipe,
    MessageService,
    provideZonelessChangeDetection(),
    // zone.js was what routed an unhandled rejection or a throw inside a timer to the ErrorHandler.
    // Without these listeners those errors never reach PostHog and the telemetry degrades silently.
    provideBrowserGlobalErrorListeners(),
    // Dev-only (self-gated on ngDevMode): re-checks every view after change detection, including
    // OnPush ones that were not marked dirty, so a binding that only ever repainted because some
    // unrelated tick swept the tree fails loudly here instead of showing a stale screen in
    // production. Add `interval: 100` while smoke-testing to also catch views nothing refreshes.
    provideCheckNoChangesConfig({ exhaustive: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withPreloading(ViewportPreloadingStrategy),
      withNavigationErrorHandler(reportNavigationError),
      // Most navigations in this app are fire-and-forget. A rejected navigation promise nobody
      // awaits is an unhandled rejection, and the global error listeners above now forward those
      // to PostHog — so resolve it to `false` and let the handler report the failure once, with
      // the URL that failed instead of a bare stack.
      withRouterConfig({ resolveNavigationPromiseOnError: true }),
      ...(isElectron ? [withHashLocation()] : [])
    ),
    // A deal room page reads its id once: another room's link (an account's deals) must build it anew.
    { provide: RouteReuseStrategy, useClass: RecreateOnParamChangeStrategy },
    provideHttpClient(withInterceptors([authInterceptor, noCacheInterceptor]), withFetch()),
    provideMarkdown(),
    provideOptimus({
      theme: {
        // Boot from the stamped hint; DesignVersionService applies successful live evaluations.
        preset: initializeDesignVersion(),
        options: {
          darkModeSelector: '.dark-mode',
          // PrimeNG's styles go into a cascade layer; ours stay outside one, and an unlayered rule
          // beats every layered rule regardless of how the selector is written. That is what lets a
          // plain class override a component without `!important` — see `10` §2.
          //
          // `reset` is declared first, so it is the weakest: our scrollbar and third-party-chrome
          // rules stop competing with component styles, which is the one case where a reset and a
          // component library reliably fight.
          //
          // The inversion to know about: for `!important` declarations the layer order is reversed,
          // so PrimeNG's importants now outrank ours. It ships fourteen of them, all geometry, on
          // six components — measured, listed and checked in `10` §2.
          cssLayer: {
            name: 'optimus',
            order: 'reset, optimus',
          },
        },
      },
    }),
    providePostHogErrorHandler(),
    provideAppInitializer(runAuthInitializer),
    provideAppInitializer(runFeatureFlagInitializer),
    provideAppInitializer(runResearchInitializer),
  ],
};
